import {
  INCOME_PER_PROPERTY,
  type MovementType,
  type PlayerBuildingCounts,
  type PlayerCapacityStats,
  type PlayerId,
} from "@dailywars/core/engine";
import type { ReactNode } from "react";

import { Infotip, InfotipContent, InfotipProvider, InfotipTrigger } from "#/components/ui/infotip";
import { cn } from "#/lib/utils";

import { CoinMoneyIcon } from "./CoinMoneyIcon";
import { MovementHudIcon } from "./MovementHudIcon";

interface GameHUDProps {
  dayNumber: number;
  funds: Record<PlayerId, number>;
  capacityStats: PlayerCapacityStats;
  buildingCounts: PlayerBuildingCounts;
}

function HudInfotipStat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Infotip>
      <InfotipTrigger aria-label={label} render={<span className="inline-flex cursor-default" />}>
        {children}
      </InfotipTrigger>
      <InfotipContent side="bottom">{label}</InfotipContent>
    </Infotip>
  );
}

function HudStackedStat({
  icon,
  label,
  wideIconFirst,
}: {
  icon: ReactNode;
  label: ReactNode;
  wideIconFirst?: boolean;
}) {
  const iconOrder = wideIconFirst ? "sm:order-1" : "sm:order-2";
  const labelOrder = wideIconFirst ? "sm:order-2" : "sm:order-1";
  return (
    <span className="inline-flex flex-col items-center gap-0.5 sm:flex-row sm:items-center sm:gap-1">
      <span className={cn("inline-flex", iconOrder)}>{icon}</span>
      <span className={cn("inline-flex tabular-nums", labelOrder)}>{label}</span>
    </span>
  );
}

const MOVEMENT_TYPES: readonly MovementType[] = ["foot", "vehicle", "air"];

const MOVEMENT_TOOLTIP: Record<MovementType, string> = {
  foot: "Foot units",
  vehicle: "Vehicle units",
  air: "Air units",
};

function dailyIncome(counts: PlayerBuildingCounts[PlayerId]): number {
  return (
    (counts.barracks + counts.factories + counts.bases + counts.cities + counts.airports) *
    INCOME_PER_PROPERTY
  );
}

function CapacityStat({
  playerId,
  movementType,
  current,
  cap,
  wideIconFirst,
}: {
  playerId: PlayerId;
  movementType: MovementType;
  current: number;
  cap: number;
  wideIconFirst?: boolean;
}) {
  return (
    <HudInfotipStat label={MOVEMENT_TOOLTIP[movementType]}>
      <HudStackedStat
        wideIconFirst={wideIconFirst}
        icon={<MovementHudIcon movementType={movementType} owner={playerId} />}
        label={
          <span
            className={cn("text-xs sm:text-sm", current >= cap && "text-red-500")}
          >{`${current}/${cap}`}</span>
        }
      />
    </HudInfotipStat>
  );
}

function CapacityStats({
  playerId,
  stats,
  mirrorOrder,
  wideIconFirst,
}: {
  playerId: PlayerId;
  stats: PlayerCapacityStats[PlayerId];
  mirrorOrder?: boolean;
  wideIconFirst?: boolean;
}) {
  const movementTypes = mirrorOrder ? [...MOVEMENT_TYPES].reverse() : MOVEMENT_TYPES;
  return (
    <>
      {movementTypes.map((movementType) => {
        const { current, cap } = stats[movementType];
        if (cap <= 0) return null;
        return (
          <CapacityStat
            key={movementType}
            playerId={playerId}
            movementType={movementType}
            current={current}
            cap={cap}
            wideIconFirst={wideIconFirst}
          />
        );
      })}
    </>
  );
}

function FundsStat({
  funds,
  income,
  wideIconFirst,
}: {
  funds: number;
  income: number;
  wideIconFirst?: boolean;
}) {
  const incomeLabel = income > 0 ? `+${income}/day income` : "No income";
  return (
    <HudInfotipStat label={incomeLabel}>
      <HudStackedStat
        wideIconFirst={wideIconFirst}
        icon={<CoinMoneyIcon size="sm" />}
        label={<span className="text-xs sm:text-sm">{funds}</span>}
      />
    </HudInfotipStat>
  );
}

function PlayerBarContent({
  side,
  playerId,
  funds,
  stats,
  income,
}: {
  side: "left" | "right";
  playerId: PlayerId;
  funds: number;
  stats: PlayerCapacityStats[PlayerId];
  income: number;
}) {
  const wideIconFirst = side === "left";
  const capacity = (
    <CapacityStats
      playerId={playerId}
      stats={stats}
      mirrorOrder={side === "right"}
      wideIconFirst={wideIconFirst}
    />
  );
  const fundsStat = <FundsStat funds={funds} income={income} wideIconFirst={wideIconFirst} />;
  return side === "left" ? (
    <>
      {fundsStat}
      {capacity}
    </>
  ) : (
    <>
      {capacity}
      {fundsStat}
    </>
  );
}

function PlayerBar({
  playerId,
  funds,
  stats,
  income,
  side,
}: {
  playerId: PlayerId;
  funds: number;
  stats: PlayerCapacityStats[PlayerId];
  income: number;
  side: "left" | "right";
}) {
  const justify = side === "left" ? "justify-start" : "justify-end";
  return (
    <span
      className={`flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1 tabular-nums sm:gap-x-3 ${justify}`}
    >
      <PlayerBarContent
        side={side}
        playerId={playerId}
        funds={funds}
        stats={stats}
        income={income}
      />
    </span>
  );
}

export function GameHUD({ dayNumber, funds, capacityStats, buildingCounts }: GameHUDProps) {
  return (
    <InfotipProvider delay={300}>
      <div className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-foreground sm:gap-3 sm:px-5 sm:py-3">
        <PlayerBar
          playerId="p1"
          funds={funds.p1}
          stats={capacityStats.p1}
          income={dailyIncome(buildingCounts.p1)}
          side="left"
        />
        <span className="shrink-0">Day {dayNumber}</span>
        <PlayerBar
          playerId="p2"
          funds={funds.p2}
          stats={capacityStats.p2}
          income={dailyIncome(buildingCounts.p2)}
          side="right"
        />
      </div>
    </InfotipProvider>
  );
}
